import { Component, useRef, useState } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { List as ImmutableList } from 'immutable';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Text } from 'foundations-components/components/Text';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { SearchInput as SearchInputComponent } from 'foundations-components/transitional/components/SearchInput';
import { ContactsIcon, ListsIcon } from 'foundations-assets';
import UISelect from 'UIComponents/input/UISelect';
import { stripDiacritics } from 'UIComponents/utils/Diacritics';
import { getDisplayNameForUser, getSearchHelpText } from '../../utils/searchUtils';
import { sortTeamsAlphabetically } from '../../utils/teamUtils';
import { trackOverviewSearch } from '../../utils/usageTracker';
import { getUserActivationStatus } from '../../utils/userUtils';
import { CLOSE_OVERVIEW_SEARCH, OPEN_OVERVIEW_SEARCH, SELECT_OVERVIEW_SEARCH } from '../../constants/Events';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TEAMS = 'Teams';
const USERS = 'Users';

// Must remain class component:
// react-dom.development.js:114 Warning: Function components cannot be given refs.
class SearchInput extends Component {
  render() {
    return /*#__PURE__*/_jsx(SearchInputComponent, Object.assign({
      use: "on-dark"
    }, this.props));
  }
}
const DropdownFooter = ({
  includeDeactivatedUsersInSearch,
  setIncludeDeactivatedUsersInSearch
}) => {
  return /*#__PURE__*/_jsx(Checkbox, {
    checked: includeDeactivatedUsersInSearch,
    onChange: event => {
      var _event$target;
      return setIncludeDeactivatedUsersInSearch((_event$target = event.target) === null || _event$target === void 0 ? void 0 : _event$target.checked);
    },
    size: "small",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "userSearch.showDeactivatedUsers"
    })
  });
};
const renderCustomSelectOption = option => {
  const {
    loading,
    text,
    group,
    help
  } = option;
  if (loading) {
    return null;
  }

  // 'Users' and 'Teams' option headers
  if (text === USERS || text === TEAMS) {
    return /*#__PURE__*/_jsx(Flex, {
      paddingBlock: "200",
      paddingInline: "500",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: text === USERS ? 'userSearch.label.users' : 'userSearch.label.teams'
      })
    });
  }

  // Search results for users and teams
  const dataTestId = text.replaceAll(' ', '_');
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    paddingBlock: "200",
    paddingInline: "500",
    children: [/*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      "data-test-id": `user-and-team-search-${dataTestId}`,
      flexDirection: "row",
      children: [/*#__PURE__*/_jsx(IconHolder, {
        icon: group.text === USERS ? ContactsIcon : ListsIcon,
        marginInlineEnd: "300",
        paddingInline: "0"
      }), /*#__PURE__*/_jsx(Text, {
        children: text
      })]
    }), group.text === USERS && help]
  });
};
// This implementation was inspired by https://git.hubteam.com/HubSpot/ui-addon-teams
// With significant changes to allow for proper async search and mixing of user and team results
const UserAndTeamSearch = ({
  buttonUse,
  handleSelectUserOrTeam,
  isDealStageView,
  menuWidth,
  ownersMap,
  teams = ImmutableList(),
  userIdToOwnerIdMap,
  userIdsWithPerms,
  usersMap,
  usersToTeamMap
}) => {
  // Used to prevent handleOpenChange from getting called multiple times due to UISelect bug
  const selectIsOpen = useRef(false);
  const [includeDeactivatedUsersInSearch, setIncludeDeactivatedUsersInSearch] = useState(false);
  const getTeamOptions = teamList => teamList.map(team => ({
    text: team.get('name'),
    value: `${team.get('id')}`,
    icon: 'lists'
  }));
  const handleSelectedOptionChange = ({
    target: {
      value
    }
  }) => {
    var _value$group;
    trackOverviewSearch(isDealStageView, SELECT_OVERVIEW_SEARCH);
    if (((_value$group = value.group) === null || _value$group === void 0 ? void 0 : _value$group.text) === TEAMS) {
      return handleSelectUserOrTeam({
        teamId: Number(value === null || value === void 0 ? void 0 : value.value)
      });
    }
    const userId = value.value;
    const userOwnerId = userIdToOwnerIdMap.get(userId);
    const userTeam = usersToTeamMap.get(userId);
    if (userTeam) {
      return handleSelectUserOrTeam({
        ownerId: userOwnerId,
        teamId: userTeam.get('id')
      });
    }
    return handleSelectUserOrTeam({
      ownerId: userOwnerId
    });
  };
  const handleOpenChange = ({
    target: {
      value
    }
  }) => {
    if (selectIsOpen.current !== value) {
      selectIsOpen.current = value;
      trackOverviewSearch(isDealStageView, value ? OPEN_OVERVIEW_SEARCH : CLOSE_OVERVIEW_SEARCH);
    }
  };
  const constructUserAndTeamOptions = (input, callback) => {
    const PAGE_SIZE = 10;
    if (!input.length) {
      callback(null);
      return;
    }
    const sortedTeamOptions = getTeamOptions(sortTeamsAlphabetically(teams)).toJS();
    const filteredTeamOptions = sortedTeamOptions.filter(team => {
      return stripDiacritics(team.text.toLowerCase()).includes(input.toLowerCase());
    });
    const userOptions = userIdsWithPerms.map(userId => {
      const user = usersMap.get(userId);
      const displayName = getDisplayNameForUser(user);
      const isUserDeactivated = getUserActivationStatus(ownersMap, userId) === 'inactive';
      const helpText = getSearchHelpText(usersToTeamMap, user, isUserDeactivated);
      return {
        text: displayName,
        value: userId,
        email: user.get('email'),
        icon: 'contact',
        help: helpText,
        isActive: !isUserDeactivated
      };
    }).filter(user => {
      return stripDiacritics(user.text.concat(user.email).toLowerCase()).includes(input);
    }).toArray();

    // NOTE: Pagination on UISelect not supported due to the nested nature of "Teams" and "Users" options
    // Instead, limit results to 10 teams and 10 users for perf on large portals to avoid rendering too many options
    const filledOptions = [{
      text: TEAMS,
      options: filteredTeamOptions.slice(0, PAGE_SIZE)
    }, {
      text: USERS,
      options: userOptions.slice(0, PAGE_SIZE)
    }];
    callback(null, {
      options: filledOptions
    });
  };

  // Filter out deactivated users if the checkbox is not checked
  const filterUserAndTeamOptions = options => {
    return options.filter(option => {
      // Do not filter out teams that match the input string
      if (option.group && option.group.text === TEAMS) {
        return true;
      }
      return includeDeactivatedUsersInSearch || option.isActive;
    });
  };
  return /*#__PURE__*/_jsx(View, {
    inlineSize: `${menuWidth}px`,
    marginBlockEnd: "200",
    children: /*#__PURE__*/_jsx(UISelect, {
      anchorType: "input",
      buttonUse: buttonUse,
      "data-test-id": "user-and-team-search",
      dropdownFooter: /*#__PURE__*/_jsx(DropdownFooter, {
        includeDeactivatedUsersInSearch: includeDeactivatedUsersInSearch,
        setIncludeDeactivatedUsersInSearch: setIncludeDeactivatedUsersInSearch
      }),
      filterOptions: filterUserAndTeamOptions,
      inputComponent: SearchInput,
      loadOptions: constructUserAndTeamOptions,
      menuWidth: menuWidth,
      onOpenChange: handleOpenChange,
      onSelectedOptionChange: handleSelectedOptionChange,
      optionRenderer: option => renderCustomSelectOption(option),
      placeholder: I18n.text('userSearch.placeholder'),
      placement: "bottom right"
    })
  });
};
export default UserAndTeamSearch;